import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<Theme>(() => {
    // Check localStorage first for immediate hydration
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("theme");
      if (stored === "light" || stored === "dark") {
        return stored;
      }
    }
    return "light"; // Default theme
  });
  
  // Use user.id to prevent unnecessary re-fetches on window focus
  const userId = user?.id;

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Load theme from database when user is available
  useEffect(() => {
    const loadThemeFromDB = async () => {
      if (!userId) return;

      try {
        const { data, error } = await supabase
          .from("settings")
          .select("settings")
          .eq("user_id", userId)
          .eq("category", "general")
          .maybeSingle();

        if (error) throw error;

        if (data?.settings && typeof data.settings === "object" && !Array.isArray(data.settings)) {
          const settings = data.settings as Record<string, unknown>;
          if (typeof settings.darkMode === "boolean") {
            setThemeState(settings.darkMode ? "dark" : "light");
          }
        }
      } catch (error) {
        logger.error("Error loading theme from database:", error);
      }
    };

    loadThemeFromDB();
  }, [userId]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
